/**
 * Zod schema for the inline sharing-form dialect (topic_garden only, gated by
 * `topicGardenEnabled`).
 *
 * Defines and validates the authoring structure for the per-type
 * `default_share_form.yaml` and a garden topic's saved `customTemplates`: the
 * document is a `forms` list, one entry per intent, and each entry's `form` is
 * an ordered sequence of SLOTS defined INLINE at their point of use — instead of
 * the classic `wrappedBody` + separate `fields` list.
 *
 * A slot's YAML key IS its type, and the type names are the ones the author sees
 * in the builder rather than HTML form terminology:
 *
 *   text     literal text, static in the sent message
 *   goal     text about the topic's impression goal
 *   optional text the sender opts into as they compose
 *   select   the end user picks a preset option, or writes one in
 *   fillIn   the end user types free text
 *
 * Parsing lifts that key into a `type` tag, so everything downstream reads
 * `slot.type` and never the slot's shape.
 *
 * Every slot carries an `id` that no other slot in the config uses, across all
 * its forms, so any slot can be named on its own. A config saved before that
 * rule — a text slot written as a bare string with no id, or one intent's
 * answered slot reusing another intent's id — does not parse, and falls back
 * to the type default like any other form the parser no longer accepts.
 *
 * ```yaml
 * form:
 *   - text:
 *       id: ZQ9vb1XgPZ3k0w
 *       expr: 'Hi, it''s '
 *   - select:
 *       id: oM8oXVQRlxhvyw
 *       prompt: Greeting
 *       options: [Hey, Hi, Hello]
 *       writeIn: { label: Other }
 * ```
 *
 * A `select` offers preset `options` plus an optional `writeIn`: the free-text
 * alternative the end user can type instead of picking one, with an optional
 * `label` and `maxlength`. A preset option that inserts a primitive is
 * written braced (`'{_profileName}'`). A pure free-text field is a `fillIn` slot
 * (id/prompt/placeholder/maxlength) — no parent select. Both answerable types
 * require a `prompt`: the question the sender sees while composing.
 *
 * One document shape covers every type: `SharingConfigSchema`. A document
 * declares which intents it is for — the three message intents, or the two news
 * ones — and must carry that whole group, the same rule the classic templates
 * file follows through its `bodyWraps`.
 *
 * A `goal` slot is a `text` variant tied to the topic's impression goal: the
 * prose about how many people the run has reached so far and the goal it is
 * racing toward, referencing primitives only (`{_runImpressionPeople}`,
 * `{_impressionGoal}`, …). It composes as plain body text like any other text
 * slot; the type exists so the builder can gate offering it on the topic having
 * an impression goal (and so a future garden-native sharing flow can drop the
 * block when the goal is cleared). It is also the one home of the news
 * impression-race prose.
 *
 * This module is the schema only. The parsing that LOWERS a validated form into
 * the runtime `TemplateSet` shape — plus the editor helpers — lives alongside in
 * `./FormLogic` (`FormLogic`), which consumes these schemas.
 */

import { z } from 'zod';
import { EnvironmentLogic, ExpressionLogic } from './lang';
import type { TopicTypeId } from '@/modules/topic/types';
import {
  ALL_INTENTS,
  INTENT_GROUPS,
  intentGroupOf,
} from '@/modules/template/schema';
import type { AddressingModeGate } from '@/modules/share';
import type { SharingModeGate } from '@/modules/template/types';
import { BASE64_RE as UUID80_BASE64_RE } from '@/uuid_extensions/uuid80';

// A form's authored `intent` IS the runtime intent — no translation anywhere,
// and the intent groups are the classic schema's, so the two dialects can never
// disagree about which intents make a whole document.
const INTENT_GROUP_LIST = INTENT_GROUPS.map(g => g.join(' / ')).join(', or ');

/**
 * A slot's `id` names that one slot, and an answered slot's answer is stored
 * under it. It is not meant for people to read: a base64url UUID80
 * (`@/uuid_extensions/uuid80`), whose alphabet has no curly brackets. The
 * builder generates one for every slot it creates (`SlotLogic.newSlotId`); a
 * field code looks up by a fixed id pads a word with underscores to the same
 * 14 characters (`allowedDetail_`).
 */
export function isSlotUUID80b64(id: string): boolean {
  return UUID80_BASE64_RE.test(id);
}

/**
 * Whether `name` is bound to a value other than a procedure in the frames
 * enclosing a form for a topic of `type` (`formEnclosingNamespace`): a primitive, or
 * a name the type defines in its `definitions.yaml` (with no type, any type).
 * A form can read such a name without binding it.
 */
function isValueBoundOutsideOfFormFrame(
  name: string,
  type?: TopicTypeId
): boolean {
  const value = EnvironmentLogic.lookupVariableValue(
    EnvironmentLogic.formEnclosingNamespace(type),
    name
  );
  return value !== undefined && !EnvironmentLogic.isPrimitiveProcedure(value);
}

/**
 * The names a form for a topic of `type` can read without assigning them,
 * alphabetically, each with its doc: every name bound to a value (not a
 * procedure) where the form will be composed (`formEnclosingNamespace`), the
 * primitives and what `type` defines alike.
 */
export function insertableNames(
  type?: TopicTypeId
): readonly { readonly name: string; readonly doc?: string }[] {
  const env = EnvironmentLogic.formEnclosingNamespace(type);
  return EnvironmentLogic.namesIn(env)
    .filter(name => isValueBoundOutsideOfFormFrame(name, type))
    .sort()
    .map(name => ({ name, doc: EnvironmentLogic.docOf(env, name) }));
}

/**
 * The `_`-prefixed names a text embeds that a form for a topic of `type`
 * cannot resolve (see `isValueBoundOutsideOfFormFrame`).
 */
export function unknownPrimitives(
  text: string,
  type?: TopicTypeId
): readonly string[] {
  return ExpressionLogic.freeNames(
    ExpressionLogic.template(text, 'asWritten')
  ).filter(
    name => name.startsWith('_') && !isValueBoundOutsideOfFormFrame(name, type)
  );
}

/**
 * What is wrong with the tokens a text embeds besides the names they read: a
 * token that cannot be read, or a call to a procedure that does not exist or
 * with the wrong operands (see `ExpressionLogic.issues`), checked where a
 * form for a topic of `type` is composed, so a name `type` defines as a
 * procedure is called as that procedure.
 */
export function tokenIssues(
  text: string,
  type?: TopicTypeId
): readonly string[] {
  return ExpressionLogic.issues(
    ExpressionLogic.template(text, 'asWritten'),
    EnvironmentLogic.formEnclosingNamespace(type)
  );
}

function addTokenIssues(
  where: string,
  text: string,
  ctx: z.RefinementCtx,
  type?: TopicTypeId
): void {
  for (const issue of tokenIssues(text, type)) {
    ctx.addIssue({ code: 'custom', message: `${where}: ${issue}` });
  }
}

// ── Slot shapes ─────────────────────────────────────────────────────────────

/**
 * Which addressing modes offer a slot — see `AddressingModeGate`. Absent means
 * every mode, which is why the key is optional rather than defaulted: a form
 * that never set it round-trips through the builder unchanged.
 */
const AddressingModesSchema = z
  .enum([
    'all',
    'dm',
    'group-text',
  ] as const satisfies readonly AddressingModeGate[])
  .optional();

/**
 * Which sharing modes offer a slot — see `SharingModeGate`. A fresh share and
 * a reshare compose the same intent, so this is how one form says which of its
 * slots belong to which: the pitch a sender writes when the topic is new to
 * everyone, or the story a resharer passes on. Absent means both, which is
 * what a form that draws no such distinction leaves every slot at.
 */
const SharingModesSchema = z
  .enum([
    'all',
    'fresh',
    'reshare',
  ] as const satisfies readonly SharingModeGate[])
  .optional();

/**
 * The question an answerable slot (`select`, `fillIn`) puts to the sender as
 * they compose — "Greeting", "What would you bring?". Required: without it the
 * sender is shown no question and cannot tell what is being asked, so a
 * blank or whitespace-only prompt is rejected rather than defaulted.
 */
const PromptSchema = z
  .string({ required_error: 'Prompt is required' })
  .refine(prompt => prompt.trim() !== '', { message: 'Prompt is required' });

/** The free-text alternative a select offers alongside its presets. */
const WriteInSchema = z
  .object({
    label: z.string().min(1).optional(),
    placeholder: z.string().optional(),
    maxlength: z.number().int().positive().optional(),
  })
  .strict();

/**
 * The two keys a Select, Fill in or Text slot takes to feed a variable.
 *
 * `define` names the variable the slot's printed answer (or, for a Text
 * slot, its `expr` with any variables filled in) is bound to. `dontEmit`
 * keeps the slot out of the message body, so it only asks or assigns. Both
 * stay absent unless the author sets them, so a form that never used them
 * round-trips unchanged. `dontEmit` is only ever `true`: emitting is the
 * default, and turning it back on removes the key.
 *
 * Several slots may assign the same variable; the last one answered in form
 * order wins. The form-level rules (not referenced by the Text slot assigning
 * it, assigned before a Text slot reads it) are checked by `variableIssues`.
 */
const variableAssignment = {
  dontEmit: z.literal(true).optional(),
  define: z.string().optional(),
};

/**
 * Every slot's `id`: a base64 UUID80 no other slot in the whole config uses,
 * across all its forms. It is how the builder, and anything else, names one
 * slot. An answered slot's id is also the key its answer is stored under.
 */
const SlotIdSchema = z.string().min(1);

/**
 * The soft link a slot shares with its copies in the other intents' forms.
 *
 * The builder's Link action copies a slot into another intent's form and gives
 * the original and the copy the same `linkId` — a base64 UUID80, like a slot
 * id — so the set of slots carrying it, at most one per form, are the same
 * question asked in each intent. Every linked slot keeps its own `id`, so the
 * answers it stores and the Settings tab's choices that name it stay its own. Absent means the slot is linked to nothing, which is every slot a
 * form ever held before linking existed.
 */
const softLink = {
  linkId: z.string().optional(),
};

/** Literal text, rendered verbatim in the sent message. */
const TextSlotSchema = z
  .object({
    type: z.literal('text'),
    id: SlotIdSchema,
    expr: z.string().min(1),
    forSharingModes: SharingModesSchema,
    ...variableAssignment,
    ...softLink,
  })
  .strict();

/**
 * The impression-goal text: literal text like a text slot, but tagged so the
 * builder knows it depends on the topic's impression goal. Like the
 * `goalSentence` it generalizes, it may only reference names bound outside the form (primitives and the topic
 * type's definitions) — user
 * fields belong in `select` / `fillIn` slots.
 */
const GoalSlotSchema = z
  .object({
    type: z.literal('goal'),
    id: SlotIdSchema,
    expr: z.string().min(1),
    forSharingModes: SharingModesSchema,
    ...softLink,
  })
  .strict();

/**
 * An OPTIONAL piece of text: literal text that may embed primitives
 * (`' {_recipientInformalName}'`), which the sender opts into rather than
 * always receiving. Its two possible answers are the empty string and the
 * text.
 *
 * `forAddressingModes` restricts the text to one addressing mode. `dm` is for
 * text that only makes sense when the message reaches exactly one person,
 * because it names them: `{_recipientInformalName}` resolves against the first
 * recipient, so a group text carrying it would greet everyone in the batch by
 * that one name. Such a slot is offered only while each message is a
 * direct text, and is dropped from the message entirely otherwise; `group-text` is its mirror. The
 * key's absence means `all`, so a form that never cared about this dumps
 * exactly as it always did.
 *
 * Its `id` is also the key the sender's choice is stored under.
 */
const OptionalSlotSchema = z
  .object({
    type: z.literal('optional'),
    id: SlotIdSchema,
    expr: z.string().min(1),
    forAddressingModes: AddressingModesSchema,
    forSharingModes: SharingModesSchema,
    ...softLink,
  })
  .strict();

const SelectSlotSchema = z
  .object({
    type: z.literal('select'),
    id: SlotIdSchema,
    prompt: PromptSchema,
    options: z.array(z.string().min(1)).optional(),
    writeIn: WriteInSchema.optional(),
    forSharingModes: SharingModesSchema,
    ...variableAssignment,
    ...softLink,
  })
  .strict();

/** A standalone free-text field (no preset options). */
const FillInSlotSchema = z
  .object({
    type: z.literal('fillIn'),
    id: SlotIdSchema,
    prompt: PromptSchema,
    placeholder: z.string().optional(),
    maxlength: z.number().int().positive().optional(),
    forSharingModes: SharingModesSchema,
    ...variableAssignment,
    ...softLink,
  })
  .strict();

const TaggedSlotSchema = z.discriminatedUnion('type', [
  TextSlotSchema,
  GoalSlotSchema,
  OptionalSlotSchema,
  SelectSlotSchema,
  FillInSlotSchema,
]);

type TaggedSlot = z.infer<typeof TaggedSlotSchema>;

// ── Slot validation ─────────────────────────────────────────────────────────

// A primitive name written as a whole option, without the braces that make it
// a reference.
const BARE_NAME_RE = /^_[a-zA-Z][a-zA-Z0-9_]*$/;

/** Validate a select's preset options: duplicates + primitive references. */
function validateOptions(
  id: string,
  options: readonly string[],
  ctx: z.RefinementCtx,
  type?: TopicTypeId
): void {
  const seen = new Set<string>();
  for (const opt of options) {
    if (seen.has(opt)) {
      ctx.addIssue({
        code: 'custom',
        message: `Select "${id}" has duplicate option "${opt}"`,
      });
    }
    if (BARE_NAME_RE.test(opt)) {
      ctx.addIssue({
        code: 'custom',
        message: `Select "${id}" option "${opt}" names a primitive without braces; write it as {${opt}}`,
      });
    }
    for (const name of unknownPrimitives(opt, type)) {
      ctx.addIssue({
        code: 'custom',
        message: `Select "${id}" option "${opt}" references unknown primitive {${name}}`,
      });
    }
    addTokenIssues(`Select "${id}" option "${opt}"`, opt, ctx, type);
    seen.add(opt);
  }
}

const IDENTIFIER_RE = /^[a-zA-Z][a-zA-Z0-9_]*$/;

/** Whether `name` is bound in the base environment, to a procedure. */
export function isProcedureName(name: string): boolean {
  return (
    EnvironmentLogic.lookupVariableValue(
      EnvironmentLogic.baseEnvironment,
      name
    ) !== undefined
  );
}

/**
 * Whether a name may be used as a variable: identifier-shaped, no `_` prefix,
 * and not a primitive procedure's name, which a variable would shadow.
 */
export function isVariableName(name: string): boolean {
  return IDENTIFIER_RE.test(name) && !isProcedureName(name);
}

function badIdIssue(label: string, id: string, ctx: z.RefinementCtx): void {
  ctx.addIssue({
    code: 'custom',
    message: `${label} id "${id}" must be a base64 UUID80 (14 characters of A–Z a–z 0–9 - _)`,
  });
}

/**
 * The rules a slot's own contents must satisfy. One switch over the tag, so a
 * new slot type declares its validation in exactly one place — and the compiler
 * says so if it forgets.
 *
 * An unknown primitive would otherwise throw at compose time (the editor's
 * warning catches this live; this is the parse-time gate, which also retires
 * saved forms referencing primitives that no longer exist, such as the removed
 * `{_goalParagraph}`, into the fall-back-to-default path).
 */
function validateSlot(
  slot: TaggedSlot,
  ctx: z.RefinementCtx,
  type?: TopicTypeId
): void {
  if ('define' in slot && slot.define !== undefined) {
    if (!isVariableName(slot.define)) {
      ctx.addIssue({
        code: 'custom',
        message: `Variable name "${slot.define}" must be identifier-shaped, not start with "_", and not name a procedure`,
      });
    }
  }
  if (slot.linkId !== undefined && !isSlotUUID80b64(slot.linkId)) {
    ctx.addIssue({
      code: 'custom',
      message: `Link id "${slot.linkId}" must be a base64 UUID80 (14 characters of A–Z a–z 0–9 - _)`,
    });
  }
  switch (slot.type) {
    case 'text':
      if (!isSlotUUID80b64(slot.id)) badIdIssue('Text', slot.id, ctx);
      for (const name of unknownPrimitives(slot.expr, type)) {
        ctx.addIssue({
          code: 'custom',
          message: `Text references unknown primitive {${name}}`,
        });
      }
      addTokenIssues('Text', slot.expr, ctx, type);
      return;
    case 'goal':
      if (!isSlotUUID80b64(slot.id)) badIdIssue('Goal', slot.id, ctx);
      // A goal may reference only names bound outside the form — a variable here
      // would compose as literal braces.
      for (const name of ExpressionLogic.freeNames(
        ExpressionLogic.template(slot.expr, 'asWritten')
      )) {
        if (!isValueBoundOutsideOfFormFrame(name, type)) {
          ctx.addIssue({
            code: 'custom',
            message: `Goal text may only reference primitives or the topic type's definitions, found {${name}}`,
          });
        }
      }
      addTokenIssues('Goal text', slot.expr, ctx, type);
      return;
    case 'optional':
      if (!isSlotUUID80b64(slot.id)) {
        badIdIssue('Optional', slot.id, ctx);
      }
      for (const name of unknownPrimitives(slot.expr, type)) {
        ctx.addIssue({
          code: 'custom',
          message: `Optional text "${slot.expr}" references unknown primitive {${name}}`,
        });
      }
      addTokenIssues(`Optional text "${slot.expr}"`, slot.expr, ctx, type);
      return;
    case 'select': {
      if (!isSlotUUID80b64(slot.id)) {
        badIdIssue('Select', slot.id, ctx);
        return;
      }
      const options = slot.options ?? [];
      if (options.length === 0) {
        ctx.addIssue({
          code: 'custom',
          message: `Select "${slot.id}" must offer at least one preset option; use a "fillIn" slot for a pure free-text field`,
        });
      }
      validateOptions(slot.id, options, ctx, type);
      return;
    }
    case 'fillIn':
      if (!isSlotUUID80b64(slot.id)) badIdIssue('Fill-in', slot.id, ctx);
      return;
  }
}

/** The slot types, and so the YAML keys a slot may be authored under. */
export const SLOT_TYPES = [
  'text',
  'goal',
  'optional',
  'select',
  'fillIn',
] as const;

const SLOT_TYPE_SET: ReadonlySet<string> = new Set(SLOT_TYPES);

/**
 * Lift a slot's YAML key into its `type` tag — `{ select: {...} }` becomes
 * `{ type: 'select', ... }` — so everything downstream of parsing reads the tag
 * and never the shape.
 *
 * `slotsForDump` in `./FormLogic` is the exact inverse, so a form round-trips
 * through the editor unchanged.
 *
 * Anything unrecognized — a bare string, say, which has no room for an id —
 * is passed through untouched for the union to reject, which sends a form
 * written in that shape down the fall-back-to-default path.
 */
function withTypeTag(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null) return raw;
  const entries = Object.entries(raw);
  if (entries.length !== 1) return raw;
  const [type, payload] = entries[0];
  if (!SLOT_TYPE_SET.has(type)) return raw;
  if (typeof payload === 'object' && payload !== null) {
    return { type, ...payload };
  }
  return raw;
}

/**
 * One slot, tagged by the key it was authored under, in a form for a topic
 * of `type` (see `sharingConfigSchema`).
 */
function sharingFormSlotSchema(type?: TopicTypeId) {
  return z.preprocess(
    withTypeTag,
    TaggedSlotSchema.superRefine((slot, ctx) => validateSlot(slot, ctx, type))
  );
}

const SharingFormSlotSchema = sharingFormSlotSchema();

export type SlotWriteIn = z.infer<typeof WriteInSchema>;
export type TextSlot = z.infer<typeof TextSlotSchema>;
export type GoalSlot = z.infer<typeof GoalSlotSchema>;
export type OptionalSlot = z.infer<typeof OptionalSlotSchema>;
export type SelectSlot = z.infer<typeof SelectSlotSchema>;
export type FillInSlot = z.infer<typeof FillInSlotSchema>;
export type ParsedFormSlot = z.infer<typeof SharingFormSlotSchema>;

/**
 * The variable a slot assigns, or undefined when it assigns none (or is a type
 * that cannot).
 */
export function declaredVariableName(slot: ParsedFormSlot): string | undefined {
  return 'define' in slot ? slot.define : undefined;
}

/** The template texts a slot prints from: its text, or each select option. */
function templateTexts(slot: ParsedFormSlot): readonly string[] {
  switch (slot.type) {
    case 'text':
    case 'goal':
    case 'optional':
      return [slot.expr];
    case 'select':
      return slot.options ?? [];
    case 'fillIn':
      return [];
  }
}

/** Every `{name}` a slot can print, each once, in order of first use. */
export function referencedNames(slot: ParsedFormSlot): readonly string[] {
  return Array.from(
    new Set(
      templateTexts(slot).flatMap(text =>
        ExpressionLogic.freeNames(ExpressionLogic.template(text, 'asWritten'))
      )
    )
  );
}

/** How an issue names the slot whose text reads a name. */
function readerLabel(slot: ParsedFormSlot): string {
  switch (slot.type) {
    case 'select':
      return 'A select option';
    case 'optional':
      return 'Optional text';
    default:
      return 'Text';
  }
}

/**
 * The variable a garden sharing form's slots assign the allowed detail to:
 * what this sender's leg lets travel with the story of how it reached the
 * next person.
 */
export const ALLOWED_DETAIL_VARIABLE = 'allowedDetail';

/** One `{name}` a slot reads, and the slot that binds it there, by index. */
export interface SlotRead {
  readonly name: string;
  /** Undefined when nothing binds the name where it is read. */
  readonly binder: number | undefined;
}

/**
 * What each slot of a form reads, slot by slot: every `{name}` it references
 * other than a primitive, with the slot that binds the name where it is
 * read — the latest earlier slot defining it.
 * A name nothing in the form binds that `type` defines (see `isValueBoundOutsideOfFormFrame`)
 * is bound outside the form, so it is no read here either.
 * Goal text reads only primitive names, so a goal slot reads nothing here.
 */
export function slotReads(
  form: readonly ParsedFormSlot[],
  type?: TopicTypeId
): readonly (readonly SlotRead[])[] {
  const defined = new Map<string, number>();
  return form.map((slot, index) => {
    const reads =
      slot.type === 'goal'
        ? []
        : referencedNames(slot)
            .filter(name => !name.startsWith('_'))
            .map(name => ({
              name,
              binder: defined.get(name),
            }))
            .filter(
              ({ name, binder }) =>
                binder !== undefined ||
                !isValueBoundOutsideOfFormFrame(name, type)
            );
    const variable = declaredVariableName(slot);
    if (variable) defined.set(variable, index);
    return reads;
  });
}

/**
 * Whether some slot `found` accepts is reachable from slot `from` — `from`
 * itself, or a slot binding a name it reads, and so on.
 */
export function readsReach(
  reads: readonly (readonly SlotRead[])[],
  from: number,
  found: (index: number) => boolean
): boolean {
  const seen = new Set<number>();
  const stack = [from];
  while (stack.length > 0) {
    const at = stack.pop() as number;
    if (seen.has(at)) continue;
    seen.add(at);
    if (found(at)) return true;
    for (const { binder } of reads[at]) {
      if (binder !== undefined) stack.push(binder);
    }
  }
  return false;
}

/**
 * What is wrong with the variables one form assigns and reads, per slot index.
 * Text, optional text and select options are all checked.
 *
 * - A slot may not read the variable it assigns itself.
 * - A slot may only read a variable some earlier slot assigns with `define`.
 *   A slot's id is never a variable.
 *
 * Primitive names are left to the slot's own checks. Goal text may only read
 * primitive names, which `validateSlot` enforces.
 *
 * Shared by the schema and the builder (`SlotLogic.warningInStack`), so the
 * builder warns about exactly what the parser would reject.
 */
export function variableIssues(
  form: readonly ParsedFormSlot[],
  type?: TopicTypeId
): ReadonlyArray<{ index: number; message: string }> {
  const issues: { index: number; message: string }[] = [];
  const reads = slotReads(form, type).map((slotReads, index) => {
    const variable = declaredVariableName(form[index]);
    const label = readerLabel(form[index]);
    return slotReads.filter(({ name, binder }) => {
      if (name === variable) {
        issues.push({
          index,
          message: `${label} reads {${name}}, the variable it assigns`,
        });
        return false;
      }
      if (binder === undefined) {
        issues.push({
          index,
          message: `${label} reads {${name}}, which is not defined`,
        });
        return false;
      }
      return true;
    });
  });

  // The reshare story is told from the allowed detail, so the detail cannot
  // be told from the story, directly or through the names it reads.
  const namesStory = (at: number) =>
    referencedNames(form[at]).includes('_reshareStory');
  form.forEach((slot, index) => {
    if (declaredVariableName(slot) !== ALLOWED_DETAIL_VARIABLE) return;
    if (readsReach(reads, index, namesStory)) {
      issues.push({
        index,
        message: `${readerLabel(slot)} assigns {${ALLOWED_DETAIL_VARIABLE}} from {_reshareStory}, which is told from the allowed detail`,
      });
    }
  });
  return issues;
}

function formSequence(type?: TopicTypeId) {
  return z
    .array(sharingFormSlotSchema(type))
    .min(1)
    .superRefine((form, ctx) => {
      for (const { index, message } of variableIssues(form, type)) {
        ctx.addIssue({ code: 'custom', path: [index], message });
      }
    });
}

/** One intent's sharing form: the intent it is for, plus its slot sequence. */
function sharingFormSchema(type?: TopicTypeId) {
  return z.object({
    intent: z.enum(ALL_INTENTS),
    form: formSequence(type),
  });
}

/**
 * A whole sharing setup document: a `forms` list carrying one whole intent
 * group, each intent exactly once. Which group it is — the three message
 * intents or the two news ones — is the document's own to declare, exactly as
 * a classic templates file declares it through its `bodyWraps`.
 */
function buildSharingConfigSchema(type?: TopicTypeId) {
  return z
    .object({
      version: z.literal(1),
      forms: z.array(sharingFormSchema(type)).min(1),
    })
    .superRefine((config, ctx) => {
      const slotIds = new Set<string>();
      config.forms.forEach((f, formIndex) =>
        f.form.forEach((slot, slotIndex) => {
          if (slotIds.has(slot.id)) {
            ctx.addIssue({
              code: 'custom',
              path: ['forms', formIndex, 'form', slotIndex],
              message: `Slot id "${slot.id}" is used by more than one slot`,
            });
          }
          slotIds.add(slot.id);
        })
      );
      config.forms.forEach((f, formIndex) => {
        const links = new Set<string>();
        f.form.forEach((slot, slotIndex) => {
          if (slot.linkId === undefined) return;
          if (links.has(slot.linkId)) {
            ctx.addIssue({
              code: 'custom',
              path: ['forms', formIndex, 'form', slotIndex],
              message: `The ${f.intent} form links more than one slot under "${slot.linkId}"`,
            });
          }
          links.add(slot.linkId);
        });
      });
      const seen = new Set<string>();
      for (const f of config.forms) {
        if (seen.has(f.intent)) {
          ctx.addIssue({
            code: 'custom',
            message: `Duplicate intent "${f.intent}"`,
          });
        }
        seen.add(f.intent);
      }
      if (!intentGroupOf(seen)) {
        ctx.addIssue({
          code: 'custom',
          message: `forms must cover one whole intent group (${INTENT_GROUP_LIST})`,
        });
      }
    });
}

const SPEC_SCHEMAS = new Map<
  TopicTypeId | undefined,
  ReturnType<typeof buildSharingConfigSchema>
>();

/**
 * The schema of a sharing setup document for a topic of `type`, which reads
 * the names that type defines as primitives and checks a call to a name it
 * defines as a procedure as a call to that procedure. With no type, a form
 * may read any type's definitions.
 */
export function sharingConfigSchema(type?: TopicTypeId) {
  let schema = SPEC_SCHEMAS.get(type);
  if (!schema) {
    schema = buildSharingConfigSchema(type);
    SPEC_SCHEMAS.set(type, schema);
  }
  return schema;
}

export const SharingConfigSchema = sharingConfigSchema();
